import { SelectHTMLAttributes, ReactNode } from "react";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  /**
   * セレクトのラベル
   */
  label?: string;

  /**
   * プレースホルダーテキスト
   */
  placeholder?: string;

  /**
   * ヘルパーテキスト
   */
  helperText?: string;

  /**
   * エラーメッセージ
   */
  error?: string;

  /**
   * セレクトの選択肢
   */
  options?: SelectOption[];

  /**
   * セレクトの内容（optionsの代わりに使用可能）
   */
  children?: ReactNode;

  /**
   * 全幅表示
   */
  fullWidth?: boolean;
}

// Chevron Icon Component
const ChevronIcon = ({ isOpen }: { isOpen?: boolean }) => {
  return (
    <svg
      className={`w-[18px] h-[18px] transition-transform duration-200 ${
        isOpen ? "rotate-180" : ""
      }`}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.5 6.75L9 11.25L13.5 6.75"
        stroke="#05B45B"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export function Select({
  label,
  placeholder = "選択してください",
  helperText,
  error,
  options = [],
  children,
  fullWidth = false,
  disabled = false,
  className = "",
  ...props
}: SelectProps) {
  const hasError = Boolean(error);

  // Base classes
  const baseClasses =
    "font-sans rounded-lg transition-all duration-200 outline-none relative appearance-none";

  // State classes
  const stateClasses = hasError
    ? "bg-accent-50 border-accent focus:border-accent"
    : "bg-black-25 border-black-300 hover:border-primary focus:border-primary";

  // Focus ring
  const focusClasses = "focus:ring-2 focus:ring-primary focus:ring-offset-1";

  // Width classes
  const widthClass = fullWidth ? "w-full" : "w-auto min-w-[275px]";

  // Disabled classes
  const disabledClass = disabled
    ? "opacity-50 cursor-not-allowed"
    : "cursor-pointer";

  const selectClasses = `ui-select${hasError ? " ui-select--error" : ""} ${baseClasses} ${stateClasses} ${focusClasses} ${widthClass} ${disabledClass} px-2 py-2.5 pr-8 max-sm:text-[0.9375rem] text-sm border border-solid text-[#363635] ${className}`;

  // Label classes
  const labelClasses = hasError
    ? "text-accent text-[15px] leading-[23px] font-normal"
    : "text-sub text-[15px] leading-[23px] font-normal";

  // Helper/Error text classes
  const helperClasses = hasError
    ? "text-accent max-sm:text-[0.9375rem] text-[14px] leading-[23px] font-normal"
    : "text-black-400 max-sm:text-[0.9375rem] text-[14px] leading-[23px] font-normal";

  return (
    <div className={`flex flex-col gap-1 ${fullWidth ? "w-full" : ""}`}>
      {label && <label className={labelClasses}>{label}</label>}
      <div className="relative">
        <select disabled={disabled} className={selectClasses} {...props}>
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children
            ? children
            : options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </option>
              ))}
        </select>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
          <ChevronIcon />
        </div>
      </div>
      {(helperText || error) && (
        <p className={helperClasses}>{error || helperText}</p>
      )}
    </div>
  );
}
